/**
 * LivePlayByPlay — CFBD-backed live play scroll for the athlete
 * profile. Renders under the LiveGamePill only when a CFB game is
 * currently in progress. Polls the backend `/api/live/cfbd/plays/{game_id}`
 * every 10 seconds and prepends new plays as they land.
 *
 * iter560 · Part of Phase 3 of the ESPN → CFBD cutover. Kept dumb and
 * self-contained: no context, no global state, just poll + render.
 * Auto-unmounts when the parent hides (game ends / drawer closes).
 */
import { useEffect, useState, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POLL_INTERVAL_MS = 10_000;

const LivePlayByPlay = ({ gameId, active = true }) => {
  const [plays, setPlays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const seenIds = useRef(new Set());

  useEffect(() => {
    if (!active || !gameId) return undefined;
    let cancelled = false;

    const tick = async () => {
      try {
        const r = await axios.get(`${API}/live/cfbd/plays/${gameId}`, { timeout: 8000 });
        if (cancelled) return;
        const next = Array.isArray(r.data?.plays) ? r.data.plays : [];
        // Dedupe by play id so the scroll doesn't flash duplicates
        // when the CFBD upstream cache returns the same play twice.
        const filtered = next.filter((p) => {
          if (!p.id) return true;
          if (seenIds.current.has(p.id)) return false;
          seenIds.current.add(p.id);
          return true;
        });
        setPlays((prev) => {
          // If this is the first successful poll, replace wholesale
          // (initial paint); otherwise merge in new plays at the top.
          if (prev.length === 0) return next;
          return [...filtered, ...prev].slice(0, 200);
        });
        setError(null);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Live plays unavailable');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    tick();
    const id = setInterval(tick, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [gameId, active]);

  if (!active || !gameId) return null;
  if (loading && plays.length === 0) {
    return (
      <div className="mt-3 px-3 py-4 rounded-2xl bg-white/[0.03] ring-1 ring-white/10 text-center" data-testid="live-pbp-loading">
        <p className="text-[10px] uppercase tracking-widest text-white/40">Loading plays…</p>
      </div>
    );
  }
  if (error && plays.length === 0) return null;
  if (plays.length === 0) return null;

  return (
    <div className="mt-3 rounded-2xl bg-white/[0.03] ring-1 ring-white/10 overflow-hidden" data-testid="live-pbp-feed">
      <div className="px-3 py-2 flex items-center justify-between bg-white/[0.04]">
        <span className="text-[10px] uppercase tracking-widest text-white/60 font-bold flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
          Live · Play-by-Play
        </span>
        <span className="text-[10px] text-white/40">{plays.length} plays</span>
      </div>
      <div className="max-h-64 overflow-y-auto divide-y divide-white/[0.06]">
        {plays.slice(0, 40).map((p, i) => (
          <div
            key={p.id || `${p.period}-${p.clock}-${i}`}
            className={`px-3 py-2 ${p.scoring ? 'bg-amber-500/[0.08]' : ''}`}
            data-testid={`live-pbp-play-${i}`}
          >
            <div className="flex items-start justify-between gap-3">
              <p className="text-[13px] text-white/90 leading-snug flex-1">
                {p.scoring && <span className="text-amber-400 font-black mr-1.5">SCORE</span>}
                {p.text || '—'}
              </p>
              <span className="text-[10px] text-white/40 font-mono shrink-0 whitespace-nowrap">
                Q{p.period || '?'} {p.clock || ''}
              </span>
            </div>
            {(p.offense_score !== null && p.offense_score !== undefined) && (
              <p className="mt-1 text-[10px] text-white/50">
                {p.offense || ''} {p.offense_score} — {p.defense_score} {p.defense || ''}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default LivePlayByPlay;
